import React, { FC, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import useService from '../../hooks/service.hook';
import useSnackbarHelper from '../../hooks/snackbar.hook';
import PaymentSubscriptionService from '../../services/payment-subscription.service';
import { palette } from '../../theme';

export interface ManageTeamMembersDialogProps {
  open: boolean;
  onClose: () => void;
  teamId: string;
  includedMembers: number;
  additionalMembers: number;
  pricePerAdditionalMember: number;
  basePrice: number;
  onSaved: () => void;
}

const ManageTeamMembersDialog: FC<ManageTeamMembersDialogProps> = ({
  open,
  onClose,
  teamId,
  includedMembers,
  additionalMembers,
  pricePerAdditionalMember,
  basePrice,
  onSaved,
}) => {
  const { showSnackbar } = useSnackbarHelper();
  const paymentSubscriptionService = useService(PaymentSubscriptionService);
  const [totalMembers, setTotalMembers] = useState(additionalMembers + includedMembers);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setTotalMembers(additionalMembers + includedMembers);
      setSaving(false);
    }
  }, [open, additionalMembers, includedMembers]);

  const previewPrice = pricePerAdditionalMember * Math.max(totalMembers - includedMembers, 0) + basePrice;

  const handleSave = () => {
    setSaving(true);
    paymentSubscriptionService
      .updateMembers(teamId, totalMembers)
      .then(() => {
        showSnackbar('Successfully updated additional members', 'success');
        onSaved();
        onClose();
      })
      .catch((err: any) => {
        setSaving(false);
        if (err?.response?.status === 403) {
          showSnackbar('You currently have too many members to lower your subscription.', 'error');
        } else {
          showSnackbar('Error while updating members for subscription', 'error');
        }
      });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        <Typography component="span" variant="h6" sx={{ fontWeight: 700 }}>
          Manage Additional Athletes
        </Typography>
        <IconButton onClick={onClose} aria-label="Close" size="small" disabled={saving}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography color={palette.text.secondary}>Members Included (free)</Typography>
            <Typography fontWeight={600}>{includedMembers}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography color={palette.text.secondary}>
              Additional Members (${pricePerAdditionalMember} / member)
            </Typography>
            <Typography fontWeight={600}>{additionalMembers}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography color={palette.text.secondary}>Total Team Price</Typography>
            <Typography fontWeight={600}>
              ${(additionalMembers * pricePerAdditionalMember + basePrice).toFixed(2)}
            </Typography>
          </Box>

          <Divider sx={{ my: 1.5 }} />

          <Typography variant="subtitle1" fontWeight={700}>
            Add / Remove Athletes
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 3, mt: 1 }}>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Total Team Members
              </Typography>
              <TextField
                id="manage-team-members-total"
                type="number"
                size="small"
                sx={{ width: 150 }}
                value={totalMembers}
                disabled={saving}
                onChange={(evt) => {
                  const next = parseInt(evt.target.value, 10);
                  if (Number.isNaN(next) || next < includedMembers) return;
                  setTotalMembers(next);
                }}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: includedMembers }}
              />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Price
              </Typography>
              <Typography fontWeight={700}>${previewPrice.toFixed(2)}</Typography>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={18} color="inherit" /> : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManageTeamMembersDialog;
